import "@/App.css";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SeoManager } from "@/components/Seo";
import { KIDS_ROUTE_ENABLED } from "@/constants/site";
import { Header } from "@/components/Header";
import { ScrollHero } from "@/components/ScrollHero";
// Below-the-fold homepage sections: lazy chunks, mounted only when scrolled
// near (LazySection below) — keeps them out of the initial JS parse, the
// mobile CPU budget that drives LCP/TBT.
const AditiSection = lazy(() =>
  import("@/components/AditiSection").then((m) => ({ default: m.AditiSection }))
);
const PapaSection = lazy(() =>
  import("@/components/PapaSection").then((m) => ({ default: m.PapaSection }))
);
const FeaturesSection = lazy(() =>
  import("@/components/FeaturesSection").then((m) => ({ default: m.FeaturesSection }))
);
const RouteCheckSection = lazy(() =>
  import("@/components/RouteCheckSection").then((m) => ({ default: m.RouteCheckSection }))
);
const EnvironmentSection = lazy(() =>
  import("@/components/EnvironmentSection").then((m) => ({ default: m.EnvironmentSection }))
);
const BottomCta = lazy(() =>
  import("@/components/BottomCta").then((m) => ({ default: m.BottomCta }))
);
const Footer = lazy(() =>
  import("@/components/Footer").then((m) => ({ default: m.Footer }))
);
// Route-level code splitting: every non-homepage page loads on demand so the
// initial bundle only pays for the landing page (LCP route stays eager).
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PressPage = lazy(() => import("@/pages/PressPage"));
const ThanePage = lazy(() => import("@/pages/ThanePage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const TrustSafetyPage = lazy(() => import("@/pages/TrustSafetyPage"));
const LawEnforcementPage = lazy(() => import("@/pages/LawEnforcementPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const KidsPage = lazy(() => import("@/pages/KidsPage"));
const NriPage = lazy(() => import("@/pages/NriPage"));
const InvestorsPage = lazy(() => import("@/pages/InvestorsPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const SafetyTrustPage = lazy(() => import("@/pages/SafetyTrustPage"));
const BlogIndexPage = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const SosLocationPage = lazy(() => import("@/pages/SosLocationPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const LegalDocPage = lazy(() =>
  import("@/pages/LegalPage").then((m) => ({ default: m.LegalDocPage }))
);
const NriSection = lazy(() =>
  import("@/components/NriSection").then((m) => ({ default: m.NriSection }))
);
import { PRIVACY, TERMS, DPDP, DELETE_ACCOUNT } from "@/pages/legalContent";
const VolunteerBand = lazy(() =>
  import("@/components/VolunteerBand").then((m) => ({ default: m.VolunteerBand }))
);

// Mounts below-the-fold sections only when scrolled near (600px look-ahead),
// so their JS chunks download and execute off the critical path. minHeight
// reserves space to avoid layout shift while the chunk loads.
const LazySection = ({ children, minHeight = 400 }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
};

const LandingPage = () => (
  <div id="top" data-testid="landing-page">
    <Header />
    <main>
      <ScrollHero />
      <LazySection minHeight={820}>
        <RouteCheckSection />
      </LazySection>
      <LazySection minHeight={620}>
        <AditiSection />
      </LazySection>
      <LazySection minHeight={620}>
        <PapaSection />
      </LazySection>
      <LazySection minHeight={220}>
        <VolunteerBand />
      </LazySection>
      <LazySection minHeight={760}>
        <FeaturesSection />
      </LazySection>
      <LazySection minHeight={700}>
        <EnvironmentSection />
      </LazySection>
      <LazySection minHeight={520}>
        <NriSection />
      </LazySection>
      <LazySection minHeight={460}>
        <BottomCta />
      </LazySection>
    </main>
    <LazySection minHeight={420}>
      <Footer />
    </LazySection>
  </div>
);

const AppRoutes = () => (
  <Suspense fallback={<div className="min-h-screen bg-[#F5F1E6]" />}>
    <Routes>
    <Route path="/" element={<LandingPage />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/press" element={<PressPage />} />
    <Route path="/thane" element={<ThanePage />} />
    <Route path="/careers" element={<CareersPage />} />
    <Route path="/trust-and-safety" element={<TrustSafetyPage />} />
    <Route path="/law-enforcement-guidelines" element={<LawEnforcementPage />} />
    <Route path="/security" element={<SecurityPage />} />
    <Route path="/plans" element={<PlansPage />} />
    {KIDS_ROUTE_ENABLED && <Route path="/kids" element={<KidsPage />} />}
    <Route path="/for-nri-families" element={<NriPage />} />
    <Route path="/investors" element={<InvestorsPage />} />
    <Route path="/partners" element={<PartnersPage />} />
    <Route path="/safety-trust" element={<SafetyTrustPage />} />
    <Route path="/blog" element={<BlogIndexPage />} />
    <Route path="/blog/:slug" element={<BlogPostPage />} />
    <Route path="/invite" element={<InvitePage />} />
    <Route path="/invite/:token" element={<InvitePage />} />
    <Route path="/s/:code" element={<SosLocationPage />} />
    <Route path="*" element={<NotFoundPage />} />
    <Route path="/women-safety" element={<CategoryPage path="/women-safety" />} />
    <Route path="/kids-safety" element={<CategoryPage path="/kids-safety" />} />
    <Route path="/elder-safety" element={<CategoryPage path="/elder-safety" />} />
    <Route path="/family-safety" element={<CategoryPage path="/family-safety" />} />
    <Route path="/privacy" element={<LegalDocPage doc={PRIVACY} />} />
    <Route path="/terms" element={<LegalDocPage doc={TERMS} />} />
    <Route path="/dpdp-consent" element={<LegalDocPage doc={DPDP} />} />
    <Route path="/delete-account" element={<LegalDocPage doc={DELETE_ACCOUNT} />} />
    <Route path="/admin" element={<AdminPage />} />
    </Routes>
  </Suspense>
);

// Page transitions apply to marketing/trust pages ONLY. /admin (and any future
// app/emergency flow) renders instantly with no wrapper, and reduced-motion
// users get no transition either.
const AnimatedRoutes = () => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const isMarketing = location.pathname !== "/admin";

  if (!isMarketing || reduceMotion) {
    return <AppRoutes />;
  }
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        <AppRoutes />
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <SeoManager />
          <AnimatedRoutes />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
